import { useCallback, useEffect, useState } from 'react'
import { EvaluationsStars } from './EvaluationStars'

import { SelectSearch } from './SelectSearch'
import { Select } from './Select'
import { ModalConfirmation } from './ModalConfirmation'
import { ModalAlert } from './ModalAlert'

import Evaluation360 from '../../API/Evaluation/Evaluation360'
import employeeMapper from '../../Utils/EmployeeMapper.js'
import Loading from '../Loading/Loading'
import { evaluation360 } from '../../i18n/translatedMessages'
import { useIntl } from 'react-intl'

const workRelationshipEs = [
	{ id: 'peer', value: 'Mi Compañero de Equipo' },
	{ id: 'team', value: 'Mi Jefe' },
	{ id: 'boss', value: 'Mi Liderado' },
]

const workRelationshipPt = [
	{ id: 'peer', value: 'Meu Companheiro de Equipe' },
	{ id: 'team', value: 'Meu Chefe' },
	{ id: 'boss', value: 'Meu Liderado' },
]

export function Main() {
	const getLanguage = useIntl()
	const language = getLanguage.locale === 'PT-BR' ? 'pt' : 'es'
	const workRelationship = language === 'pt' ? workRelationshipPt : workRelationshipEs
	const accountId = JSON.parse(localStorage.getItem('user')).id
	const companyId = JSON.parse(localStorage.getItem('user')).companyId

	const [loading, setLoading] = useState(false)
	const [loadingEmployeeList, setLoadingEmployeeList] = useState(false)

	const [relationship, setRelationship] = useState(null)
	const [employee, setEmployee] = useState(null)
	const [error, setError] = useState(false)
	const [evaluation, setEvaluation] = useState([])
	const [employeeList, setEmployeeList] = useState([])
	const [showConfirmationModal, setShowConfirmationModal] = useState(false)

	const [template, setTemplate] = useState(null)

	const getRelationshipOptions = (employeeId, accountId) => {
		if (employeeId === accountId) {
			return [{ id: 'auto', value: language === 'pt' ? 'Autoavaliação' : 'Autoevaluación' }]
		}
		return workRelationship
	}

	const handleRelationshipChange = selectedOption => {
		setRelationship(selectedOption)
	}

	const handleChooseEmployee = async option => {
		setEvaluation([])
		setTemplate(null)
		setRelationship(null)
		setEmployee(option)
	}

	useEffect(() => {
		const fetchEvaluationData = async () => {
			if (employee && relationship) {
				try {
					setLoading(true)
					const response = await Evaluation360.criteriasByEmployee(employee.id)
					setTemplate(response)
				} catch (error) {
					console.error('Erro ao buscar dados de avaliação:', error)
					setError('Erro ao buscar dados.')
				} finally {
					setLoading(false)
				}
			}
		}

		fetchEvaluationData()
	}, [employee, relationship])

	const handleEvaluation = event => {
		setEvaluation(prevEvaluation => {
			const index = prevEvaluation.findIndex(e => e.criteriaId === event.criteriaId)

			if (index !== -1) {
				const updatedEvaluation = [...prevEvaluation]
				updatedEvaluation[index] = event
				return updatedEvaluation
			} else {
				return [...prevEvaluation, event]
			}
		})
	}

	const isEvaluationComplete = () => {
		return template?.criteria?.every(criteria => {
			const evaluationItem = evaluation.find(item => item.criteriaId === criteria.criteriaId)
			return evaluationItem && evaluationItem.dataset !== null && evaluationItem.dataset !== undefined && evaluationItem.dataset !== 0
		})
	}

	const handleSendEvaluation = async () => {
		const { id } = JSON.parse(localStorage.getItem('user'))

		if (!relationship) {
			setError(true)
			return
		}

		try {
			setLoading(true)
			const relanshionshipCreated = await Evaluation360.createRelationship({
				targetId: employee.targetId,
				employeeId: id,
				templateId: template.templateId,
				relation: relationship.id,
			})

			const evaluationsData = {
				respondentId: relanshionshipCreated.respondentId,
				targetId: employee.targetId,
				employeeId: id,
				templateId: template.templateId,
				responses: evaluation.map(item => ({
					criteriaId: item.criteriaId,
					response: item.dataset,
				})),
			}

			await Evaluation360.createEvaluation(evaluationsData)

			setShowConfirmationModal(true)
			setEvaluation([])
			setTemplate(null)
			setRelationship(null)
			setEmployee(null)
		} catch (error) {
			console.error('Erro ao enviar avaliação:', error)
		} finally {
			setLoading(false)
		}
	}

	const loadData = useCallback(async () => {
		try {
			setLoadingEmployeeList(true)
			const respEmployeeList = await Evaluation360.getEmployeesWithTemplates(companyId)
			setEmployeeList(respEmployeeList.map(employeeMapper.toDomain))
		} catch (error) {
			console.error('loadData: ', error)
		} finally {
			setLoadingEmployeeList(false)
		}
	}, [])

	useEffect(() => {
		loadData()
	}, [])

	const handleCancel = () => {
		setEvaluation([])
		setTemplate(null)
		setRelationship(null)
		setEmployee(null)
	}

	return (
		<div>
			<h1 className='tw-font-bold tw-text-2xl'>{evaluation360.addEvalutation360}</h1>
			<p>{evaluation360.evaluationEEDesc1}</p>
			<p>{evaluation360.evaluationEEDesc2}</p>

			{loadingEmployeeList && <Loading />}

			<div className='tw-mr-4  tw-pr-4 tw-bg-white tw-rounded-md border tw-p-5'>
				<div className='tw-flex tw-gap-2 tw-mb-2'>
					<div className='tw-w-full tw-px-2'>
						<label>{evaluation360.evaluatedEmployeesTitle}</label>
						<SelectSearch
							list={employeeList}
							option={employee}
							onChange={handleChooseEmployee}
							placeholder={evaluation360.inputPlaceholderSelectDefault}
							name='employee'
							className='tw-w-[100%] tw-bg-white'
						/>
					</div>
					<div className='tw-w-full tw-px-2'>
						<label>{evaluation360.jobRelationshop}</label>
						<Select
							list={getRelationshipOptions(employee?.id, accountId)}
							option={relationship}
							onChange={handleRelationshipChange}
							placeholder={evaluation360.inputPlaceholderSelectDefault}
							name='relationship'
							className='tw-w-[100%] tw-bg-white'
						/>
						{error && !relationship && <span className='tw-text-error-500 tw-text-sm'>{evaluation360.requiredField}</span>}
					</div>
				</div>
				{loading ? (
					<Loading />
				) : (
					<>
						{employee && relationship && (
							<div className='tw-grid tw-grid-cols-4 tw-p-3'>
								<div className='tw-font-bold'>{evaluation360.criterionTitle}</div>
								<div className='tw-col-span-2 tw-font-bold'>{evaluation360.descriptionTitle}</div>
								<div className='tw-font-bold'>Nota</div>
							</div>
						)}
						<div className='tw-h-[580px] tw-overflow-y-auto'>
							{!employee && (
								<div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-overflow-y-scroll'>
									<h5 className='tw-font-bold tw-text-center tw-text-gray-600'>{evaluation360.selectEmployee}</h5>
								</div>
							)}

							{!employee && relationship && (
								<div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-overflow-y-scroll'>
									<h5 className='tw-font-bold tw-text-center tw-text-gray-600'>{evaluation360.emptyEmployeeSelect}.</h5>
								</div>
							)}
							{employee && !relationship && (
								<div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-overflow-y-scroll'>
									<h5 className='tw-font-bold tw-text-center tw-text-gray-600'>{evaluation360.emptyRelationSelect}.</h5>
								</div>
							)}

							{employee && relationship && !template?.criteria && (
								<div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-overflow-y-scroll'>
									<h5 className='tw-font-bold tw-text-center tw-text-gray-600'>{evaluation360.emptyEvaluation}.</h5>
								</div>
							)}

							{template?.criteria && (
								<div className='tw-space-y-5 tw-pr-2'>
									{template.criteria.map(item => (
										<div key={item.criteriaId} className='tw-grid tw-grid-cols-4 tw-p-3 border-bottom tw-items-center'>
											<div>{item.criteriaName}</div>
											<div className='tw-col-span-2'>{item.criteriaDesc}</div>
											<div className='tw-flex tw-items-center'>
												<div className='border tw-rounded-xl'>
													<EvaluationsStars
														stars={evaluation.filter(el => el.criteriaId === item.criteriaId).map(el => el.dataset)[0] || 0}
														onClick={handleEvaluation}
														criteriaId={item.criteriaId}
													/>
												</div>
											</div>
										</div>
									))}
									<div className='tw-flex tw-gap-2 tw-justify-end'>
										<button
											onClick={handleCancel}
											className='border-0 tw-rounded-full tw-py-2 tw-px-3 disabled:tw-bg-white disabled:tw-text-gray-400 tw-text-purple tw-bg-white hover:tw-bg-purple-700 hover:tw-text-white active:tw-bg-purple-700'
										>
											Cancelar
										</button>
										<button
											onClick={handleSendEvaluation}
											disabled={!isEvaluationComplete()}
											className='border-0 tw-rounded-full tw-py-2 tw-px-3 disabled:tw-bg-gray-200 disabled:tw-text-gray-400 tw-bg-purple tw-text-white hover:tw-bg-purple-700 active:tw-bg-purple-700'
										>
											{evaluation360.saveNewEvaluation}
										</button>
									</div>
								</div>
							)}
						</div>
					</>
				)}
			</div>

			<ModalConfirmation show={showConfirmationModal} setShow={setShowConfirmationModal} />
			<ModalAlert show={false} setShow={() => {}} />
		</div>
	)
}
